import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { slideService, getLocalizedValue } from '@/services/api';

const normalizeInternalSlidePath = (value) => {
  const raw = (value || '').trim();
  if (!raw.startsWith('/')) return '/';
  const path = raw.toLowerCase();
  if (path.startsWith('/doctor')) return '/doctors';
  if (path.startsWith('/special')) return '/specialties';
  if (path.startsWith('/clinic')) return '/clinics';
  if (path.startsWith('/hospital')) return '/';
  return raw;
};

const resolveExternalSlideUrl = (value) => {
  const raw = (value || '').trim();
  if (!raw || raw.startsWith('/')) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const resolveSlideAction = (slide) => {
  if (slide.doctor_id) {
    return { type: 'internal', to: `/doctors/${slide.doctor_id}` };
  }
  if (slide.clinic_id) {
    return { type: 'internal', to: `/clinics/${slide.clinic_id}` };
  }
  if (slide.hospital_id) {
    return { type: 'internal', to: '/' };
  }
  const targetUrl = (slide.url || '').trim();
  if (!targetUrl) {
    return { type: 'internal', to: '/doctors' };
  }
  const externalUrl = resolveExternalSlideUrl(targetUrl);
  if (externalUrl) {
    return { type: 'external', href: externalUrl };
  }
  return { type: 'internal', to: normalizeInternalSlidePath(targetUrl) };
};

const resolveSlideImage = (slide) => {
  const firstMedia = Array.isArray(slide.media) && slide.media.length > 0
    ? slide.media[0]
    : null;
  if (typeof firstMedia === 'string' && firstMedia.trim()) {
    return firstMedia.trim();
  }
  if (firstMedia && typeof firstMedia === 'object') {
    return firstMedia.url || firstMedia.thumb || firstMedia.icon || '';
  }
  return slide.image || '';
};

const PromotionsBanner = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'ar';
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch slides from API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await slideService.getAll();
        
        if (response.data?.success && response.data?.data) {
          const rows = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data.data.slides)
              ? response.data.data.slides
              : Array.isArray(response.data.data.items)
                ? response.data.data.items
                : [];
          // Filter only enabled slides and sort by order
          const enabledSlides = rows
            .filter(slide => slide.enabled && !!resolveSlideImage(slide))
            .sort((a, b) => a.order - b.order);
          setSlides(enabledSlides);
        }
      } catch (error) {
        console.error('Failed to fetch slides:', error);
        // Silently fail - just don't show banner if endpoint doesn't exist yet
      } finally {
        setLoading(false);
      }
    };
    
    fetchSlides();
  }, []);

  const nextSlide = useCallback(() => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    if (slides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  }, [slides.length]);

  // Auto-rotate slides
  useEffect(() => {
    if (!isPaused && slides.length > 1) {
      const interval = setInterval(nextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [isPaused, nextSlide, slides.length]);

  // Loading state
  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-2xl shadow-lg" data-testid="promotions-banner-loading">
        <Skeleton className="w-full h-48 md:h-64" />
      </div>
    );
  }

  // No slides available
  if (slides.length === 0) {
    return null;
  }

  const slide = slides[currentSlide];
  const imageUrl = resolveSlideImage(slide);
  const text = getLocalizedValue(slide.text, lang);
  const buttonText = getLocalizedValue(slide.button, lang);
  const action = resolveSlideAction(slide);

  return (
    <div 
      className="relative overflow-hidden rounded-2xl shadow-lg"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="promotions-banner"
      dir="ltr"
    >
      {/* Slide Image - Always LTR to prevent flip */}
      <div className="relative w-full h-48 md:h-64 lg:h-80">
        <img 
          src={imageUrl}
          alt={text || `Slide ${currentSlide + 1}`}
          className="w-full h-full object-cover"
          style={{ objectFit: slide.image_fit || 'cover', transform: 'none' }}
          data-testid={`slide-image-${currentSlide}`}
        />
        
        {/* Overlay for text */}
        {(text || buttonText) && (
          <div 
            className={`absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center`}
            style={{
              justifyContent: slide.text_position?.includes('end') ? 'flex-end' : 'flex-start',
              alignItems: slide.text_position?.includes('top') ? 'flex-start' : 
                         slide.text_position?.includes('bottom') ? 'flex-end' : 'center'
            }}
          >
            <div className="p-6 md:p-8 max-w-lg">
              {text && (
                <h3 
                  className="text-xl md:text-2xl lg:text-3xl font-bold mb-3 text-white"
                  style={{ color: slide.text_color || '#ffffff' }}
                >
                  {text}
                </h3>
              )}
              {buttonText && (
                <Button 
                  asChild 
                  className="mt-2"
                  style={{ 
                    backgroundColor: slide.button_color || undefined,
                    color: slide.button_color ? '#ffffff' : undefined
                  }}
                  data-testid="promo-cta-button"
                >
                  {action.type === 'external' ? (
                    <a href={action.href} target="_blank" rel="noopener noreferrer">
                      {buttonText}
                    </a>
                  ) : (
                    <Link to={action.to}>
                      {buttonText}
                    </Link>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Navigation Arrows */}
        {slides.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white h-10 w-10 rounded-full"
              onClick={prevSlide}
              data-testid="promo-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white h-10 w-10 rounded-full"
              onClick={nextSlide}
              data-testid="promo-next"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        {/* Dots Indicator */}
        {slides.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === currentSlide 
                    ? 'bg-white w-8' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                style={{ 
                  backgroundColor: idx === currentSlide 
                    ? (slide.indicator_color || '#ffffff') 
                    : undefined 
                }}
                data-testid={`promo-dot-${idx}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionsBanner;
